import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DatePicker } from '@nucleus-commerce/admin-kit/date-time';
import { GetPlatformV1AuditLogsActorType } from '@nucleus-commerce/api-client/platform';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';

import { useOrganizationOptions } from '@/features/organizations';
import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { filterFormFor, searchFromFilterForm } from '../api/mappers';
import {
  auditLogFilterFormSchema,
  type AuditLogFilterForm,
  type AuditLogSearch,
} from '../schemas/audit-log-search';

// Radix Select has no empty value; this stands for "no filter".
const ANY = '__any';

export interface AuditLogFiltersProps {
  search: AuditLogSearch;
  onApply: (search: Record<string, string>) => void;
}

/** Organization, actor type, action (exact or a `prefix.`), entity type and a UTC day range. */
export function AuditLogFilters({ search, onApply }: AuditLogFiltersProps) {
  const { t } = useTranslation('auditLog');
  const organizations = useOrganizationOptions();
  const form = useForm<AuditLogFilterForm>({
    resolver: useI18nResolver(auditLogFilterFormSchema),
    values: filterFormFor(search),
  });

  const onSubmit = form.handleSubmit((values) => onApply(searchFromFilterForm(values)));

  return (
    <Form {...form}>
      <form
        onSubmit={onSubmit}
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6 lg:items-end"
        aria-label={t('filters.label')}
        noValidate
      >
        <FormField
          control={form.control}
          name="organizationId"
          render={({ field }) => (
            <FormItem className="lg:col-span-2">
              <FormLabel>{t('filters.organization')}</FormLabel>
              <Select
                value={field.value === '' ? ANY : field.value}
                onValueChange={(value) => field.onChange(value === ANY ? '' : value)}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent className="max-h-72">
                  <SelectItem value={ANY}>{t('filters.anyOrganization')}</SelectItem>
                  {(organizations.data ?? []).map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="actorType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('filters.actorType')}</FormLabel>
              <Select
                value={field.value === '' ? ANY : field.value}
                onValueChange={(value) => field.onChange(value === ANY ? '' : value)}
              >
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={ANY}>{t('filters.anyActor')}</SelectItem>
                  {Object.values(GetPlatformV1AuditLogsActorType).map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`actorType.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="action"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('filters.action')}</FormLabel>
              <FormControl>
                <Input placeholder={t('filters.actionPlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="entityType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('filters.entityType')}</FormLabel>
              <FormControl>
                <Input placeholder={t('filters.entityTypePlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="hidden lg:block" />
        <FormField
          control={form.control}
          name="from"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('filters.from')}</FormLabel>
              <FormControl>
                <DatePicker timeZone="UTC" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="to"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('filters.to')}</FormLabel>
              <FormControl>
                <DatePicker timeZone="UTC" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex gap-2 sm:col-span-2 lg:col-span-4 lg:justify-end">
          <Button type="button" variant="ghost" onClick={() => onApply({})}>
            {t('filters.clear')}
          </Button>
          <Button type="submit">{t('filters.apply')}</Button>
        </div>
      </form>
    </Form>
  );
}
